import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { demoStore } from "./demoStore";
import { useDataChanged } from "../useDataChanged";
import { useI18n } from "../i18n";
import "./DemoAlarm.css";

/** Checkpoint at which the demo scan confirms its critical finding. */
const CRITICAL_AT = 3;

/** The climax of the demo: the moment the agent confirms the critical
 *  vulnerability, the screen edges flash red, an alert slides in and the bell
 *  shakes. Fires once per demo run, on the transition into the checkpoint. */
export function DemoAlarm() {
  const { t } = useI18n();
  // Seed with the current checkpoint so a reload mid-demo doesn't replay it.
  const prevRef = useRef<number>(demoStore.active ? (demoStore.getState().scan?.checkpoint ?? -1) : -1);
  const [shown, setShown] = useState<{ host: string; key: number } | null>(null);

  const check = useCallback(() => {
    const cp = demoStore.active ? (demoStore.getState().scan?.checkpoint ?? -1) : -1;
    const prev = prevRef.current;
    prevRef.current = cp;
    if (prev < CRITICAL_AT && cp >= CRITICAL_AT) {
      setShown({ host: demoStore.getState().host ?? "", key: Date.now() });
    }
    if (cp < 0) setShown(null);
  }, []);
  useDataChanged(check);

  useEffect(() => {
    if (!shown) return;
    document.body.classList.add("demo-alarm");
    const shake = window.setTimeout(() => document.body.classList.remove("demo-alarm"), 1900);
    const hide = window.setTimeout(() => setShown(null), 5400);
    return () => {
      window.clearTimeout(shake);
      window.clearTimeout(hide);
      document.body.classList.remove("demo-alarm");
    };
  }, [shown]);

  if (!shown) return null;
  return createPortal(
    <>
      <div className="demo-alarm-vignette" key={`v${shown.key}`} aria-hidden />
      <div className="demo-alarm-toast" key={`t${shown.key}`} role="alert">
        <span className="demo-alarm-toast__icon" aria-hidden>
          <span className="demo-alarm-toast__ring" />
          🔴
        </span>
        <div className="demo-alarm-toast__text">
          <p className="demo-alarm-toast__title">{t("tour.alarm.title")}</p>
          <p className="demo-alarm-toast__body">{t("tour.alarm.body", { host: shown.host })}</p>
        </div>
      </div>
    </>,
    document.body,
  );
}
